import { serializeThirdPartyResponsible } from '@risksmart-app/shared/src/forms/issue-assessments/thirdPartyResponsibleCodec';
import { Issue_Assessment_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { Requiredish } from 'src/components/form';
import { z } from 'zod';

import {
  CustomAttributeDataSchema,
  NullableNonFutureStringDateSchema,
  NullableStringDateSchema,
  TagsAndDepartmentsSchema,
  UserOptionSchema,
} from '../../../../../schemas/global';

export const ResponsibleThirdPartyTokenSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('registered'),
    value: z.string().uuid(),
    label: z.string().min(1),
  }),
  z.object({
    kind: z.literal('adHoc'),
    value: z.string().min(1),
    label: z.string().min(1),
  }),
]);

export type ResponsibleThirdPartyToken = z.infer<
  typeof ResponsibleThirdPartyTokenSchema
>;

const createResponsibleThirdPartiesSchema = (
  originalTypedNames?: string | null
) =>
  z.array(ResponsibleThirdPartyTokenSchema).superRefine((tokens, context) => {
    try {
      serializeThirdPartyResponsible(
        tokens
          .filter((token) => token.kind === 'adHoc')
          .map((token) => token.value),
        originalTypedNames
      );
    } catch (error) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          error instanceof Error
            ? error.message
            : 'Invalid responsible third party names',
      });
    }
  });

export const createIssueAssessmentSchema = (
  originalTypedNames?: string | null
) =>
  z
    .object({
      Severity: z.number().nullish(),
      CertifiedIndividual: UserOptionSchema.nullish(),
      IssueType: z.string().nullish(),
      ActualCloseDate: NullableNonFutureStringDateSchema,
      TargetCloseDate: NullableStringDateSchema,
      Status: z.nativeEnum(Issue_Assessment_Status_Enum).nullish(),
      PolicyOwnerCommentary: z.string().nullish(),
      PolicyOwner: UserOptionSchema.nullish(),
      PolicyBreach: z.boolean().nullish(),
      AssociatedControlIds: z.array(z.object({ value: z.string().uuid() })),
      RegulatoryBreach: z.boolean().nullish(),
      RegulationsBreach: z.string().nullish(),
      Reportable: z.boolean().nullish(),
      PoliciesBreached: z.string().nullish(),
      PoliciesBreachedIds: z.array(z.object({ value: z.string().uuid() })),
      Rationale: z.string().nullish(),
      IssueCausedByThirdParty: z.boolean().nullish(),
      SystemResponsible: z.string().nullish(),
      RegulationsBreached: z.string().nullish(),
      RegulationsBreachedIds: z.array(z.object({ value: z.string().uuid() })),
      ThirdPartyResponsible:
        createResponsibleThirdPartiesSchema(originalTypedNames),
      IssueCausedBySystemIssue: z.boolean().nullish(),
    })
    .and(CustomAttributeDataSchema)
    .and(TagsAndDepartmentsSchema);

export const IssueAssessmentSchema = createIssueAssessmentSchema();

export type IssueAssessmentFields = Requiredish<
  z.infer<typeof IssueAssessmentSchema>
>;

export const defaultValues: IssueAssessmentFields = {
  ActualCloseDate: null,
  ThirdPartyResponsible: [],
  TargetCloseDate: null,
  PolicyOwnerCommentary: null,
  AssociatedControlIds: [],
  CertifiedIndividual: null,
  IssueCausedBySystemIssue: null,
  IssueCausedByThirdParty: null,
  IssueType: null,
  tags: [],
  departments: [],
  PoliciesBreached: null,
  PolicyOwner: null,
  PolicyBreach: null,
  PoliciesBreachedIds: [],
  Rationale: null,
  RegulatoryBreach: null,
  Reportable: null,
  Severity: null,
  Status: Issue_Assessment_Status_Enum.Pending,
  SystemResponsible: null,
  RegulationsBreached: null,
  RegulationsBreachedIds: [],
  RegulationsBreach: null,
  CustomAttributeData: null,
};
